
import { Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { DateFieldOptions } from "./types";

interface CRMDateFilterProps {
  startDate: Date | undefined;
  endDate: Date | undefined;
  onStartDateChange: (date: Date | undefined) => void;
  onEndDateChange: (date: Date | undefined) => void;
  dateField: string;
  onDateFieldChange: (field: string) => void;
}

export const CRMDateFilter = ({ 
  startDate, 
  endDate, 
  onStartDateChange, 
  onEndDateChange,
  dateField,
  onDateFieldChange
}: CRMDateFilterProps) => {
  const dateFields = [
    { value: "all_dates", label: "Tous" },
    { value: "date_adhesion", label: "Adhésion" },
    { value: "date_signature", label: "Signature" },
    { value: "date_abonnement_newsletter", label: "Newsletter" }
  ];

  return (
    <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">Filtrer par:</span>
        <select
          value={dateField}
          onChange={(e) => onDateFieldChange(e.target.value)}
          className="text-sm border rounded p-1"
        >
          {dateFields.map((field) => (
            <option key={field.value} value={field.value}>{field.label}</option>
          ))}
        </select>
      </div>
      
      <div className="flex items-center gap-2">
        <span className="text-sm">Du:</span>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={"outline"}
              className={cn(
                "w-[130px] justify-start text-left font-normal",
                !startDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {startDate ? format(startDate, "dd/MM/yyyy") : "Date début"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-background border border-input shadow-md">
            <Calendar
              mode="single"
              selected={startDate}
              onSelect={onStartDateChange}
              initialFocus
              locale={fr}
              className="bg-white"
            />
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex items-center gap-2">
        <span className="text-sm">Au:</span>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={"outline"}
              className={cn(
                "w-[130px] justify-start text-left font-normal",
                !endDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {endDate ? format(endDate, "dd/MM/yyyy") : "Date fin"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 bg-background border border-input shadow-md">
            <Calendar
              mode="single"
              selected={endDate}
              onSelect={onEndDateChange}
              initialFocus
              locale={fr}
              className="bg-white"
            />
          </PopoverContent>
        </Popover>
      </div>
      
      {(startDate || endDate) && (
        <Button 
          variant="ghost" 
          className="h-8 px-2" 
          onClick={() => {
            onStartDateChange(undefined);
            onEndDateChange(undefined);
          }}
        >
          Réinitialiser
        </Button>
      )}
    </div>
  );
};
